// ============================================================================
// SOURCE DETAIL VIEW - Aligned with Interviewer module styling
// Features: Smart Doc view, facet filtering, entity creation, warm palette
// ============================================================================

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { sourcesApi, type SourceWithFacets, type DetectedFacet } from '@/lib/api/client'
import { PdfViewer } from './PdfViewer'
import { analyzeMultiPass } from '@/lib/analyzer'
import { renderPdfToImages } from '@/lib/pdf/extractor'
import { CONTENT_TYPES } from '@/lib/analyzer/types'
import { FACET_MAP, getTargetEntityTypes } from '@/lib/analyzer/facetSchema'
import { FACET_TYPE_CONFIG, FACET_CATEGORY_CONFIG, type FacetCategory, type FacetType } from '@/types/interviewer'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  AlertCircle,
  Activity,
  Target,
  Users,
  TrendingUp,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Link as LinkIcon,
  HelpCircle,
  CheckCircle,
  MessageCircle,
  FileText,
  ArrowLeft,
  Plus,
  ExternalLink,
  Loader2,
  ChevronDown,
  ChevronRight,
  X,
  Eye,
  List,
  Sparkles,
  Filter,
  Briefcase,
  BarChart,
  RefreshCw,
  Layers,
  type LucideIcon,
} from 'lucide-react'
import { ReviewBoardView } from './ReviewBoardView'

// ============================================================================
// CATEGORY ICON MAPPING - Maps icon names to Lucide components
// ============================================================================

const CATEGORY_ICONS: Record<FacetCategory, LucideIcon> = {
  market: Target,
  business: Briefcase,
  solution: Lightbulb,
  insights: MessageCircle,
}

// Icon mapping for facet type icons (from FACET_TYPE_CONFIG icon string names)
const ICON_MAP: Record<string, LucideIcon> = {
  AlertCircle,
  Activity,
  RefreshCw,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Users,
  Link: LinkIcon,
  HelpCircle,
  CheckCircle,
  TrendingUp,
  MessageCircle,
  Briefcase,
  Target,
  Sparkles,
  BarChart,
  FileText,
}

/**
 * Get facet style from the shared FACET_TYPE_CONFIG
 * Falls back to 'insights' category for unknown types
 */
function getFacetStyle(facetTypeId: string): { category: FacetCategory; color: string; icon: LucideIcon } {
  const config = FACET_TYPE_CONFIG[facetTypeId as FacetType]

  if (config) {
    return {
      category: config.category,
      color: config.color,
      icon: ICON_MAP[config.icon] || FileText,
    }
  }

  // Fallback for unknown types - try to infer from common patterns
  const lowerType = facetTypeId.toLowerCase()

  // Market types (customers, problems, competition)
  if (lowerType.includes('customer') || lowerType.includes('problem') ||
      lowerType.includes('pain') || lowerType.includes('competitor') ||
      lowerType.includes('segment') || lowerType.includes('persona')) {
    return { category: 'market', color: '#5c8a6b', icon: Target }
  }

  // Business types (strategy, decisions, model)
  if (lowerType.includes('decision') || lowerType.includes('goal') ||
      lowerType.includes('strategy') || lowerType.includes('revenue') ||
      lowerType.includes('channel') || lowerType.includes('metric')) {
    return { category: 'business', color: '#4a90a4', icon: Briefcase }
  }

  // Solution types (product, features, delivery)
  if (lowerType.includes('feature') || lowerType.includes('product') ||
      lowerType.includes('solution') || lowerType.includes('milestone')) {
    return { category: 'solution', color: '#c9a227', icon: Lightbulb }
  }

  // Default to insights
  return { category: 'insights', color: '#6c757d', icon: MessageCircle }
}

// ============================================================================
// STATUS STYLING
// ============================================================================

const STATUS_STYLES = {
  pending: { label: 'Pending', bgClass: 'bg-warm-100', textClass: 'text-warm-700', borderColor: '#c9a227' },
  accepted: { label: 'Accepted', bgClass: 'bg-green-50', textClass: 'text-green-700', borderColor: '#5c8a6b' },
  rejected: { label: 'Rejected', bgClass: 'bg-warm-50', textClass: 'text-warm-500', borderColor: '#6c757d' },
  mapped: { label: 'Entity Created', bgClass: 'bg-blue-50', textClass: 'text-blue-700', borderColor: '#4a90a4' },
}

// ============================================================================
// FACET CARD COMPONENT - Matching Interviewer FacetCard styling
// ============================================================================

interface FacetCardProps {
  facet: DetectedFacet
  isSelected: boolean
  onClick: () => void
  onAccept: () => void
  onReject: () => void
  onCreateEntity: () => void
  isCreating?: boolean
  showActions?: boolean
}

function FacetCard({
  facet,
  isSelected,
  onClick,
  onAccept,
  onReject,
  onCreateEntity,
  isCreating,
  showActions = true,
}: FacetCardProps) {
  const style = getFacetStyle(facet.facetTypeId)
  const Icon = style.icon
  const facetInfo = FACET_MAP.get(facet.facetTypeId)
  const statusStyle = STATUS_STYLES[facet.status as keyof typeof STATUS_STYLES] || STATUS_STYLES.pending

  return (
    <Card
      className={cn(
        'p-3 cursor-pointer transition-all border',
        isSelected ? 'ring-2 ring-solution bg-warm-50' : 'hover:bg-warm-50',
        facet.status === 'rejected' && 'opacity-50'
      )}
      style={{ borderColor: isSelected ? style.color : 'transparent' }}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start gap-2 mb-2">
        <div
          className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${style.color}20` }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: style.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className="text-xs"
              style={{ borderColor: style.color, color: style.color }}
            >
              {facetInfo?.name || facet.facetTypeId.replace(/_/g, ' ')}
            </Badge>
            <Badge
              variant="outline"
              className={cn('text-xs', statusStyle.bgClass, statusStyle.textClass)}
            >
              {statusStyle.label}
            </Badge>
            <span className="text-xs text-text-muted">
              {Math.round(facet.confidence * 100)}%
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <p className="text-sm mb-2">{facet.content}</p>

      {/* Evidence quote - warm styling */}
      <div
        className="bg-warm-100 rounded p-2 text-xs text-text-secondary italic border-l-2"
        style={{ borderColor: style.color }}
      >
        "{facet.evidence}"
      </div>

      {/* Suggested entity type */}
      {facet.suggestedEntityType && facet.status === 'pending' && (
        <div className="mt-2 pt-2 border-t border-warm-200">
          <div className="flex items-center gap-1 text-xs text-solution">
            <Sparkles className="w-3 h-3" />
            Suggested: {facet.suggestedEntityType}
          </div>
        </div>
      )}

      {/* Linked entity */}
      {facet.mappedEntityUri && (
        <div className="mt-2 pt-2 border-t border-warm-200">
          <div className="flex items-center gap-1 text-xs text-text-muted mb-1">
            <LinkIcon className="w-3 h-3" />
            Linked to ontology
          </div>
          <Badge
            variant="outline"
            className="text-xs bg-solution/5 border-solution/30"
          >
            {facet.mappedEntityType}
            <ExternalLink className="w-2.5 h-2.5 ml-1" />
          </Badge>
        </div>
      )}

      {/* Actions (when selected) */}
      {isSelected && showActions && (
        <div className="mt-3 pt-2 border-t border-warm-200 flex gap-2 flex-wrap">
          {facet.status === 'pending' && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7 gap-1 border-green-300 text-green-700 hover:bg-green-50"
                onClick={(e) => {
                  e.stopPropagation()
                  onAccept()
                }}
              >
                <CheckCircle className="w-3 h-3" />
                Accept
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs h-7 gap-1 text-text-muted"
                onClick={(e) => {
                  e.stopPropagation()
                  onReject()
                }}
              >
                <X className="w-3 h-3" />
                Reject
              </Button>
            </>
          )}
          {facet.status === 'accepted' && !facet.mappedEntityUri && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7 gap-1 border-solution/30 text-solution hover:bg-solution/5"
              onClick={(e) => {
                e.stopPropagation()
                onCreateEntity()
              }}
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-3 h-3" />
                  Create Entity
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// FACET DETAIL PANEL - Shows full facet details and linked entities
// ============================================================================

interface FacetDetailPanelProps {
  facet: DetectedFacet | null
  onClose: () => void
  onAccept: () => void
  onReject: () => void
  onCreateEntity: () => void
  isCreating?: boolean
  entityTypes: string[]
  onChangeEntityType: (type: string) => void
}

function FacetDetailPanel({
  facet,
  onClose,
  onAccept,
  onReject,
  onCreateEntity,
  isCreating,
  entityTypes,
  onChangeEntityType,
}: FacetDetailPanelProps) {
  if (!facet) return null

  const style = getFacetStyle(facet.facetTypeId)
  const Icon = style.icon
  const facetInfo = FACET_MAP.get(facet.facetTypeId)
  const statusStyle = STATUS_STYLES[facet.status as keyof typeof STATUS_STYLES] || STATUS_STYLES.pending

  return (
    <div className="w-80 border-l border-warm-200 bg-warm-50/50 p-4 overflow-y-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded flex items-center justify-center"
            style={{ backgroundColor: `${style.color}20` }}
          >
            <Icon className="w-4 h-4" style={{ color: style.color }} />
          </div>
          <div>
            <Badge
              variant="outline"
              className="text-xs"
              style={{ borderColor: style.color, color: style.color }}
            >
              {facetInfo?.name || facet.facetTypeId.replace(/_/g, ' ')}
            </Badge>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Status */}
      <div className="mb-4">
        <Badge
          variant="outline"
          className={cn('text-xs', statusStyle.bgClass, statusStyle.textClass)}
        >
          {statusStyle.label}
        </Badge>
        <span className="text-xs text-text-muted ml-2">
          {Math.round(facet.confidence * 100)}% confidence
        </span>
      </div>

      {/* Content */}
      <div className="mb-4">
        <h4 className="text-xs font-medium text-text-muted mb-1">Content</h4>
        <p className="text-sm">{facet.content}</p>
      </div>

      {/* Evidence */}
      <div className="mb-4">
        <h4 className="text-xs font-medium text-text-muted mb-1">Evidence</h4>
        <div
          className="bg-warm-100 rounded p-2 text-xs text-text-secondary italic border-l-2"
          style={{ borderColor: style.color }}
        >
          "{facet.evidence}"
        </div>
      </div>

      {/* Suggested Reason */}
      {facet.suggestedReason && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-text-muted mb-1">AI Reasoning</h4>
          <p className="text-xs text-text-secondary">{facet.suggestedReason}</p>
        </div>
      )}

      {/* Entity Type Selection */}
      {facet.status === 'pending' || facet.status === 'accepted' ? (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-text-muted mb-1">Entity Type</h4>
          <Select
            value={facet.mappedEntityType || facet.suggestedEntityType || ''}
            onValueChange={onChangeEntityType}
          >
            <SelectTrigger className="w-full h-8 text-xs">
              <SelectValue placeholder="Select entity type" />
            </SelectTrigger>
            <SelectContent>
              {entityTypes.map(type => (
                <SelectItem key={type} value={type} className="text-xs">{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {/* Linked Entity */}
      {facet.mappedEntityUri && (
        <div className="mb-4 p-3 bg-solution/5 rounded-lg border border-solution/20">
          <h4 className="text-xs font-medium text-solution mb-2 flex items-center gap-1">
            <LinkIcon className="w-3 h-3" />
            Linked Entity
          </h4>
          <div className="text-sm font-medium">{facet.mappedEntityType}</div>
          <div className="text-xs text-text-muted truncate" title={facet.mappedEntityUri}>
            {facet.mappedEntityUri.split('#').pop()}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-2 pt-4 border-t border-warm-200">
        {facet.status === 'pending' && (
          <>
            <Button
              className="w-full gap-2 bg-green-600 hover:bg-green-700"
              onClick={onAccept}
            >
              <CheckCircle className="w-4 h-4" />
              Accept Facet
            </Button>
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={onReject}
            >
              <X className="w-4 h-4" />
              Reject Facet
            </Button>
          </>
        )}
        {facet.status === 'accepted' && !facet.mappedEntityUri && (
          <Button
            className="w-full gap-2 bg-solution hover:bg-solution/90"
            onClick={onCreateEntity}
            disabled={isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Create Entity
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// SMART DOC VIEW - Document with highlighted facets and facet type filters
// ============================================================================

interface SmartDocViewProps {
  content: string
  facets: DetectedFacet[]
  selectedFacetId: string | null
  onFacetClick: (facetId: string) => void
  filterCategory: FacetCategory | 'all'
  filterStatus: string
}

function SmartDocView({
  content,
  facets,
  selectedFacetId,
  onFacetClick,
  filterCategory,
  filterStatus,
}: SmartDocViewProps) {
  // Track which facet types are filtered
  const [enabledFacetTypes, setEnabledFacetTypes] = useState<Set<string>>(new Set())
  const [showAllTypes, setShowAllTypes] = useState(true)

  // Get unique facet types in the document with counts
  const facetTypeCounts = useMemo(() => {
    const counts = new Map<string, number>()
    facets.forEach(f => {
      counts.set(f.facetTypeId, (counts.get(f.facetTypeId) || 0) + 1)
    })
    return counts
  }, [facets])

  // Initialize enabled types when facets change
  useEffect(() => {
    if (showAllTypes) {
      setEnabledFacetTypes(new Set(facetTypeCounts.keys()))
    }
  }, [facetTypeCounts, showAllTypes])

  // Toggle a facet type filter
  const toggleFacetType = (typeId: string) => {
    setShowAllTypes(false)
    setEnabledFacetTypes(prev => {
      const next = new Set(prev)
      if (next.has(typeId)) {
        next.delete(typeId)
      } else {
        next.add(typeId)
      }
      return next
    })
  }

  // Show all types
  const handleShowAll = () => {
    setShowAllTypes(true)
    setEnabledFacetTypes(new Set(facetTypeCounts.keys()))
  }

  // Filter facets based on current filters
  const visibleFacets = useMemo(() => {
    return facets.filter(f => {
      // Category filter
      if (filterCategory !== 'all') {
        const style = getFacetStyle(f.facetTypeId)
        if (style.category !== filterCategory) return false
      }
      // Status filter
      if (filterStatus !== 'all' && f.status !== filterStatus) return false
      // Facet type filter
      if (!showAllTypes && !enabledFacetTypes.has(f.facetTypeId)) return false
      return true
    })
  }, [facets, filterCategory, filterStatus, enabledFacetTypes, showAllTypes])

  // Parse markdown-style content into formatted elements
  const formatContent = useCallback((text: string): React.ReactNode[] => {
    const lines = text.split('\n')
    const elements: React.ReactNode[] = []
    let listItems: React.ReactNode[] = []

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside my-2 space-y-1">
            {listItems}
          </ul>
        )
        listItems = []
      }
    }

    lines.forEach((line, lineIdx) => {
      const trimmed = line.trim()

      // Headers
      if (trimmed.startsWith('### ')) {
        flushList()
        elements.push(
          <h3 key={`h3-${lineIdx}`} className="text-base font-semibold mt-4 mb-2 text-text-primary">
            {trimmed.slice(4)}
          </h3>
        )
      } else if (trimmed.startsWith('## ')) {
        flushList()
        elements.push(
          <h2 key={`h2-${lineIdx}`} className="text-lg font-bold mt-5 mb-2 text-text-primary border-b border-warm-200 pb-1">
            {trimmed.slice(3)}
          </h2>
        )
      } else if (trimmed.startsWith('# ')) {
        flushList()
        elements.push(
          <h1 key={`h1-${lineIdx}`} className="text-xl font-bold mt-6 mb-3 text-text-primary">
            {trimmed.slice(2)}
          </h1>
        )
      }
      // List items
      else if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || /^\d+\.\s/.test(trimmed)) {
        const content = trimmed.replace(/^[-*]\s|^\d+\.\s/, '')
        listItems.push(
          <li key={`li-${lineIdx}`} className="text-sm text-text-secondary">
            {content}
          </li>
        )
      }
      // Blockquote
      else if (trimmed.startsWith('> ')) {
        flushList()
        elements.push(
          <blockquote
            key={`quote-${lineIdx}`}
            className="border-l-3 border-solution pl-3 my-2 text-sm italic text-text-muted bg-warm-50 py-1"
          >
            {trimmed.slice(2)}
          </blockquote>
        )
      }
      // Empty line
      else if (trimmed === '') {
        flushList()
        elements.push(<div key={`br-${lineIdx}`} className="h-2" />)
      }
      // Regular paragraph
      else {
        flushList()
        elements.push(
          <p key={`p-${lineIdx}`} className="text-sm text-text-secondary my-1 leading-relaxed">
            {line}
          </p>
        )
      }
    })

    flushList()
    return elements
  }, [])

  // Find facet positions in content and highlight them
  const highlightedContent = useMemo(() => {
    // Sort facets by evidence position in content
    const facetsWithPositions = visibleFacets
      .map(f => {
        // Try exact match first, then case-insensitive
        let pos = content.indexOf(f.evidence)
        if (pos < 0) {
          pos = content.toLowerCase().indexOf(f.evidence.toLowerCase())
        }
        // Try partial match if full match fails
        if (pos < 0 && f.evidence.length > 30) {
          const partial = f.evidence.slice(0, 30)
          pos = content.toLowerCase().indexOf(partial.toLowerCase())
        }
        return { ...f, position: pos }
      })
      .filter(f => f.position >= 0)
      .sort((a, b) => a.position - b.position)

    // If no facets found in positions, just format the content
    if (facetsWithPositions.length === 0) {
      return (
        <div className="prose prose-sm max-w-none">
          {formatContent(content)}
        </div>
      )
    }

    // Build highlighted content
    const parts: React.ReactNode[] = []
    let lastEnd = 0

    facetsWithPositions.forEach((facet, idx) => {
      const style = getFacetStyle(facet.facetTypeId)
      const facetInfo = FACET_MAP.get(facet.facetTypeId)
      const evidenceStart = facet.position
      const evidenceEnd = Math.min(evidenceStart + facet.evidence.length, content.length)

      // Skip if overlapping with previous
      if (evidenceStart < lastEnd) return

      // Add text before this facet
      if (evidenceStart > lastEnd) {
        const beforeText = content.slice(lastEnd, evidenceStart)
        parts.push(
          <span key={`text-${idx}`}>{beforeText}</span>
        )
      }

      // Add highlighted facet evidence
      const isSelected = facet.id === selectedFacetId
      parts.push(
        <span
          key={`facet-${facet.id}`}
          id={`facet-highlight-${facet.id}`}
          className={cn(
            'cursor-pointer rounded-sm px-0.5 py-0.5 transition-all inline relative group',
            isSelected ? 'ring-2 ring-offset-1' : 'hover:ring-1',
            facet.status === 'mapped' && 'opacity-60'
          )}
          style={{
            backgroundColor: `${style.color}15`,
            borderBottom: `2px solid ${style.color}`,
            // @ts-expect-error CSS custom property for ring color
            '--tw-ring-color': style.color,
          }}
          onClick={(e) => {
            e.stopPropagation()
            onFacetClick(facet.id)
          }}
        >
          {content.slice(evidenceStart, evidenceEnd)}
          {/* Tooltip on hover */}
          <span className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-10 pointer-events-none">
            <span
              className="block px-2 py-1 text-xs rounded shadow-lg whitespace-nowrap max-w-xs truncate text-white"
              style={{ backgroundColor: style.color }}
            >
              <span className="font-medium">{facetInfo?.name || facet.facetTypeId}</span>
              : {facet.content.slice(0, 50)}...
            </span>
          </span>
        </span>
      )

      lastEnd = evidenceEnd
    })

    // Add remaining text
    if (lastEnd < content.length) {
      parts.push(<span key="text-end">{content.slice(lastEnd)}</span>)
    }

    return (
      <div className="text-sm leading-relaxed whitespace-pre-wrap">
        {parts}
      </div>
    )
  }, [content, visibleFacets, selectedFacetId, onFacetClick, formatContent])

  // Group facet types by category for the legend
  const facetTypesByCategory = useMemo(() => {
    const grouped: Record<FacetCategory, Array<{ typeId: string; count: number; style: ReturnType<typeof getFacetStyle> }>> = {
      market: [],
      business: [],
      solution: [],
      insights: [],
    }

    facetTypeCounts.forEach((count, typeId) => {
      const style = getFacetStyle(typeId)
      grouped[style.category].push({ typeId, count, style })
    })

    return grouped
  }, [facetTypeCounts])

  return (
    <div className="flex flex-col h-full">
      {/* Facet Type Filter Bar */}
      <div className="flex-shrink-0 p-3 bg-warm-50/80 border-b border-warm-200">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-text-muted mr-1">Filter by type:</span>

          {/* Show All button */}
          <Button
            variant={showAllTypes ? 'default' : 'outline'}
            size="sm"
            className="h-6 text-xs px-2"
            onClick={handleShowAll}
          >
            All ({facets.length})
          </Button>

          {/* Category groups */}
          {(Object.entries(FACET_CATEGORY_CONFIG) as [FacetCategory, typeof FACET_CATEGORY_CONFIG[FacetCategory]][]).map(([category, config]) => {
            const typesInCategory = facetTypesByCategory[category]
            if (typesInCategory.length === 0) return null

            return (
              <div key={category} className="flex items-center gap-1">
                <span className="text-[10px] text-text-muted ml-2">{config.label}:</span>
                {typesInCategory.map(({ typeId, count, style }) => {
                  const facetInfo = FACET_MAP.get(typeId)
                  const isEnabled = showAllTypes || enabledFacetTypes.has(typeId)
                  const Icon = style.icon

                  return (
                    <Button
                      key={typeId}
                      variant="outline"
                      size="sm"
                      className={cn(
                        'h-6 text-[10px] px-1.5 gap-1 border',
                        isEnabled ? 'opacity-100' : 'opacity-40'
                      )}
                      style={{
                        borderColor: style.color,
                        backgroundColor: isEnabled ? `${style.color}15` : 'transparent',
                        color: style.color,
                      }}
                      onClick={() => toggleFacetType(typeId)}
                    >
                      <Icon className="w-3 h-3" />
                      <span className="hidden sm:inline">{facetInfo?.name || typeId}</span>
                      <Badge
                        variant="secondary"
                        className="h-4 min-w-4 text-[9px] px-1"
                        style={{ backgroundColor: `${style.color}30` }}
                      >
                        {count}
                      </Badge>
                    </Button>
                  )
                })}
              </div>
            )
          })}
        </div>

        {/* Active filters summary */}
        {!showAllTypes && enabledFacetTypes.size < facetTypeCounts.size && (
          <div className="mt-2 text-xs text-text-muted">
            Showing {visibleFacets.length} of {facets.length} facets
            <Button
              variant="link"
              size="sm"
              className="h-4 text-xs px-1 ml-1"
              onClick={handleShowAll}
            >
              Clear filters
            </Button>
          </div>
        )}
      </div>

      {/* Document Content */}
      <div className="flex-1 overflow-y-auto p-4 bg-white dark:bg-zinc-900">
        <div className="max-w-full mx-auto">
          {highlightedContent}
        </div>
      </div>

      {/* Facet Navigation Legend */}
      {visibleFacets.length > 0 && (
        <div className="flex-shrink-0 p-2 bg-warm-50/80 border-t border-warm-200">
          <div className="flex items-center gap-3 overflow-x-auto">
            <span className="text-xs text-text-muted flex-shrink-0">Jump to:</span>
            {visibleFacets.slice(0, 10).map((facet, idx) => {
              const style = getFacetStyle(facet.facetTypeId)
              const Icon = style.icon

              return (
                <Button
                  key={facet.id}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-6 text-[10px] px-1.5 gap-1 flex-shrink-0',
                    selectedFacetId === facet.id && 'ring-1'
                  )}
                  style={{ color: style.color }}
                  onClick={() => {
                    onFacetClick(facet.id)
                    // Scroll to the facet highlight
                    document.getElementById(`facet-highlight-${facet.id}`)?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    })
                  }}
                >
                  <Icon className="w-3 h-3" />
                  {idx + 1}
                </Button>
              )
            })}
            {visibleFacets.length > 10 && (
              <span className="text-[10px] text-text-muted">+{visibleFacets.length - 10} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface SourceDetailViewProps {
  sourceId: string
  onBack: () => void
}

export function SourceDetailView({ sourceId, onBack }: SourceDetailViewProps) {
  const { accountId, projectId } = useSessionStore()
  const { claudeApiKey } = useSettingsStore()
  const [source, setSource] = useState<SourceWithFacets | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [analyzeProgress, setAnalyzeProgress] = useState<string>('')
  const [selectedFacetId, setSelectedFacetId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'doc' | 'pdf'>('list')
  const [filterCategory, setFilterCategory] = useState<FacetCategory | 'all'>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [creatingEntity, setCreatingEntity] = useState<string | null>(null)
  const [bulkCreating, setBulkCreating] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState<Set<FacetCategory>>(
    new Set(['market', 'business', 'solution', 'insights'])
  )
  const [showReviewBoard, setShowReviewBoard] = useState(false)
  const [pdfData, setPdfData] = useState<string | null>(null)
  const [loadingPdf, setLoadingPdf] = useState(false)

  const entityTypes = getTargetEntityTypes()

  // Load source
  useEffect(() => {
    if (!accountId || !projectId || !sourceId) return

    const loadSource = async () => {
      try {
        setLoading(true)
        setError(null)
        const result = await sourcesApi.get(accountId, projectId, sourceId)
        setSource(result)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load source')
      } finally {
        setLoading(false)
      }
    }

    loadSource()
  }, [accountId, projectId, sourceId])

  // Load PDF data if source is a PDF
  useEffect(() => {
    if (!accountId || !projectId || !source || source.fileType !== 'pdf') {
      setPdfData(null)
      return
    }

    const loadPdf = async () => {
      try {
        setLoadingPdf(true)
        const result = await sourcesApi.getFileData(accountId, projectId, source.id)
        setPdfData(result.fileData)
      } catch (err) {
        console.error('Failed to load PDF:', err)
        setPdfData(null)
      } finally {
        setLoadingPdf(false)
      }
    }

    loadPdf()
  }, [accountId, projectId, source?.id, source?.fileType])

  // Group facets by category
  const facetsByCategory = useMemo(() => {
    if (!source) return {} as Record<FacetCategory, DetectedFacet[]>

    const grouped: Record<FacetCategory, DetectedFacet[]> = {
      market: [],
      business: [],
      solution: [],
      insights: [],
    }

    source.facets.forEach(facet => {
      const style = getFacetStyle(facet.facetTypeId)
      grouped[style.category].push(facet)
    })

    return grouped
  }, [source])


  // Get selected facet
  const selectedFacet = useMemo(() => {
    if (!selectedFacetId || !source) return null
    return source.facets.find(f => f.id === selectedFacetId) || null
  }, [selectedFacetId, source])

  // Run analysis
  const handleAnalyze = async () => {
    if (!accountId || !projectId || !source) return

    if (!claudeApiKey) {
      setError('Please set your Claude API key in Settings')
      return
    }

    try {
      setAnalyzing(true)
      setError(null)

      // Prepare images for visual analysis if PDF
      let images: Array<{ id: string; data: string; mimeType: string; pageNumber: number; width: number; height: number }> = []

      if (source.fileType === 'pdf' && pdfData) {
        setAnalyzeProgress('Rendering PDF pages for visual analysis...')
        try {
          const pageImages = await renderPdfToImages(pdfData, { scale: 1.5, maxPages: 10 })
          images = pageImages.map((img, idx) => ({
            id: `page_${img.pageNumber}`,
            data: img.dataUrl.split(',')[1], // Remove data URL prefix
            mimeType: 'image/png',
            pageNumber: img.pageNumber,
            width: img.width,
            height: img.height,
          }))
          console.log(`[SourceDetailView] Rendered ${images.length} PDF pages as images`)
        } catch (err) {
          console.warn('[SourceDetailView] Failed to render PDF pages:', err)
          // Continue without images
        }
      }

      setAnalyzeProgress(images.length > 0
        ? `Pass 1/2: Extracting facets (${images.length} pages for visual analysis)...`
        : 'Pass 1/1: Extracting facets...')

      const result = await analyzeMultiPass(
        { apiKey: claudeApiKey },
        {
          sourceId: source.id,
          content: source.markdownContent,
          contentType: source.contentType,
          images: images.length > 0 ? images : undefined,
        }
      )

      // Check for analysis errors
      if (result.error) {
        setError(`Analysis failed: ${result.error}`)
        setAnalyzing(false)
        setAnalyzeProgress('')
        return
      }

      const visualCount = result.visualFacets?.length || 0
      const chunkCount = result.visualChunks?.length || 0
      const progressMsg = visualCount > 0
        ? `Found ${result.facets.length} text facets, ${visualCount} visual facets. Saving...`
        : `Found ${result.facets.length} facets${chunkCount > 0 ? `, ${chunkCount} visual regions detected` : ''}. Saving...`
      setAnalyzeProgress(progressMsg)
      console.log(`[SourceDetailView] Analysis results: ${result.facets.length} text facets, ${visualCount} visual facets, ${chunkCount} visual chunks`)

      if (result.facets.length > 0 || (result.visualFacets && result.visualFacets.length > 0)) {
        // Facet-first pipeline: entities are created offline via Review Board
        // Pass empty arrays for backward compatibility with API
        const transformedEntities: Array<{
          id: string
          type: string
          name: string
          description: string
          evidence: string
          confidence: number
        }> = []

        const transformedRelationships: Array<{
          from: string
          to: string
          type: string
          evidence: string
          confidence: number
        }> = []

        // Transform visual facets to API format
        const transformedVisualFacets = result.visualFacets?.map(vf => ({
          facetTypeId: vf.facetTypeId, // Already has correct field name from VisualFacet
          title: vf.title,
          content: vf.content || vf.title || '',
          evidence: vf.evidence || '',
          confidence: vf.confidence,
          // pageNumber comes from the image, but we don't have it here - API should handle null
          pageNumber: undefined,
          bboxX: vf.bbox?.x,
          bboxY: vf.bbox?.y,
          bboxWidth: vf.bbox?.width,
          bboxHeight: vf.bbox?.height,
          suggestedEntityType: vf.suggestedEntityType,
          suggestedReason: vf.suggestedReason,
        }))

        const saveResult = await sourcesApi.saveFacets(
          accountId,
          projectId,
          source.id,
          result.facets,
          transformedEntities,
          transformedRelationships,
          transformedVisualFacets // NEW: Pass visual facets
        )
        console.log(`[SourceDetailView] Saved: ${saveResult.textFacetCount || saveResult.facetCount} text facets, ${saveResult.visualFacetCount || 0} visual facets`)
      }

      const updated = await sourcesApi.get(accountId, projectId, source.id)
      setSource(updated)
      setAnalyzeProgress('Analysis complete! All facets auto-accepted.')

      // Show success message briefly
      setTimeout(() => setAnalyzeProgress(''), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed')
    } finally {
      setAnalyzing(false)
    }
  }

  // Handle facet decision
  const handleFacetDecision = async (facetId: string, action: 'accept' | 'reject', entityType?: string) => {
    if (!accountId || !projectId || !source) return

    try {
      const updated = await sourcesApi.updateFacet(accountId, projectId, source.id, facetId, {
        action,
        entityType,
      })

      setSource(prev => {
        if (!prev) return prev
        return {
          ...prev,
          facets: prev.facets.map(f => f.id === facetId ? updated : f),
          facetStats: {
            ...prev.facetStats,
            pending: prev.facetStats.pending - 1,
            accepted: prev.facetStats.accepted + (action === 'accept' ? 1 : 0),
            rejected: prev.facetStats.rejected + (action === 'reject' ? 1 : 0),
          },
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update facet')
    }
  }

  // Create entity from single facet
  const handleCreateEntity = async (facetId: string) => {
    if (!accountId || !projectId || !source) return

    try {
      setCreatingEntity(facetId)
      setError(null)

      const result = await sourcesApi.createEntityFromFacet(
        accountId,
        projectId,
        source.id,
        facetId
      )

      setSource(prev => {
        if (!prev) return prev
        return {
          ...prev,
          facets: prev.facets.map(f => f.id === facetId ? result.facet : f),
          facetStats: {
            ...prev.facetStats,
            accepted: prev.facetStats.accepted - 1,
            mapped: prev.facetStats.mapped + 1,
          },
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create entity')
    } finally {
      setCreatingEntity(null)
    }
  }

  // Bulk create entities
  const handleBulkCreateEntities = async () => {
    console.log('[DEBUG] handleBulkCreateEntities called')
    console.log('[DEBUG] accountId:', accountId, 'projectId:', projectId, 'source:', source?.id)

    if (!accountId || !projectId || !source) {
      console.log('[DEBUG] Missing required params, returning early')
      return
    }

    const acceptedFacets = source.facets.filter(f => f.status === 'accepted' && !f.mappedEntityUri)
    console.log('[DEBUG] Accepted facets without entity:', acceptedFacets.length, acceptedFacets.map(f => ({ id: f.id, status: f.status, type: f.facetTypeId })))

    if (acceptedFacets.length === 0) {
      console.log('[DEBUG] No accepted facets to create, returning early')
      return
    }

    try {
      setBulkCreating(true)
      setError(null)

      console.log('[DEBUG] Calling bulkCreateEntities API...')
      const result = await sourcesApi.bulkCreateEntities(
        accountId,
        projectId,
        source.id,
        acceptedFacets.map(f => f.id)
      )
      console.log('[DEBUG] bulkCreateEntities result:', result)
      console.log('[DEBUG] Failed results:', result.results.filter((r: any) => !r.success))

      const updated = await sourcesApi.get(accountId, projectId, source.id)
      setSource(updated)
      console.log('[DEBUG] Source updated, new facet stats:', updated.facetStats)

      if (result.failed > 0) {
        const insightOnlyFails = result.results.filter(r =>
          !r.success && r.error?.includes('insight-only')
        ).length
        const otherFails = result.failed - insightOnlyFails

        if (insightOnlyFails > 0 && otherFails === 0) {
          setError(`Created ${result.created} entities. ${insightOnlyFails} facet(s) are insight-only.`)
        } else if (otherFails > 0) {
          setError(`Created ${result.created} entities, ${result.failed} failed`)
        }
      }
    } catch (err) {
      console.error('[DEBUG] bulkCreateEntities error:', err)
      setError(err instanceof Error ? err.message : 'Failed to create entities')
    } finally {
      setBulkCreating(false)
    }
  }

  // NOTE: Facets are now auto-accepted on the backend during analysis
  // This function is no longer needed but kept for reference
  // const handleBulkAcceptAndCreate = async () => { ... }

  // Toggle category expansion
  const toggleCategory = (category: FacetCategory) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-warm-200 rounded" />
          <div className="h-64 bg-warm-200 rounded" />
        </div>
      </div>
    )
  }

  if (error && !source) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4 gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Sources
        </Button>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg border border-red-200">
          {error}
        </div>
      </div>
    )
  }

  if (!source) return null

  // Show Review Board if active
  if (showReviewBoard) {
    return (
      <ReviewBoardView
        sourceId={sourceId}
        onBack={() => setShowReviewBoard(false)}
        onComplete={async () => {
          // Reload source to get updated data
          if (accountId && projectId) {
            const updated = await sourcesApi.get(accountId, projectId, sourceId)
            setSource(updated)
          }
          setShowReviewBoard(false)
        }}
      />
    )
  }

  const contentTypeInfo = CONTENT_TYPES.find(ct => ct.id === source.contentType)

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-warm-200 bg-warm-50/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-text-muted" />
                {source.title}
              </h1>
              <p className="text-text-muted text-sm">
                {contentTypeInfo?.name} · {source.facetStats.total} facets detected
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Analyze button - facets and entities auto-created */}
            {source.status === 'pending' || source.facetStats.total === 0 ? (
              <Button onClick={handleAnalyze} disabled={analyzing} className="gap-2">
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {analyzeProgress || 'Analyzing...'}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Analyze Content
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleAnalyze} disabled={analyzing} className="gap-2">
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {analyzeProgress || 'Re-analyzing...'}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Re-analyze
                    </>
                  )}
                </Button>
                {/* Review Board button - shown after analysis */}
                <Button
                  onClick={() => setShowReviewBoard(true)}
                  className="gap-2 bg-solution hover:bg-solution/90"
                >
                  <Layers className="w-4 h-4" />
                  Review & Map
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-3 bg-red-50 text-red-700 p-3 rounded-lg text-sm border border-red-200">
            {error}
          </div>
        )}

        {/* Stats bar */}
        <div className="flex items-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FACET_CATEGORY_CONFIG.insights.color }} />
            <span className="text-sm">{source.facetStats.pending} pending</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-sm">{source.facetStats.accepted} accepted</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FACET_CATEGORY_CONFIG.solution.color }} />
            <span className="text-sm">{source.facetStats.mapped} entities</span>
          </div>

          <div className="flex-1" />

          {/* View toggle */}
          <div className="flex items-center gap-1 bg-warm-100 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-7 gap-1',
                viewMode === 'list' && 'bg-white shadow-sm text-text-primary'
              )}
              onClick={() => setViewMode('list')}
            >
              <List className="w-3.5 h-3.5" />
              List
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-7 gap-1',
                viewMode === 'doc' && 'bg-white shadow-sm text-text-primary'
              )}
              onClick={() => setViewMode('doc')}
            >
              <Eye className="w-3.5 h-3.5" />
              Doc
            </Button>
            {source.fileType === 'pdf' && pdfData && (
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-7 gap-1',
                  viewMode === 'pdf' && 'bg-white shadow-sm text-text-primary'
                )}
                onClick={() => setViewMode('pdf')}
              >
                <FileText className="w-3.5 h-3.5" />
                PDF
              </Button>
            )}
          </div>

          {/* Filters */}
          <Select value={filterCategory} onValueChange={(v) => setFilterCategory(v as FacetCategory | 'all')}>
            <SelectTrigger className="w-40 h-8">
              <Filter className="w-3.5 h-3.5 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(FACET_CATEGORY_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  <span style={{ color: cfg.color }}>{cfg.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="mapped">Mapped</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bulk create action */}
        {source.facetStats.accepted > 0 && (
          <div className="flex items-center justify-between mt-4 p-3 bg-solution/5 rounded-lg border border-solution/20">
            <div>
              <p className="font-medium text-solution">
                {source.facetStats.accepted} accepted facet{source.facetStats.accepted !== 1 ? 's' : ''} ready
              </p>
              <p className="text-xs text-text-muted">
                Create ontology entities from accepted insights
              </p>
            </div>
            <Button
              onClick={handleBulkCreateEntities}
              disabled={bulkCreating}
              className="gap-2 bg-solution hover:bg-solution/90"
            >
              {bulkCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create All Entities
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Facets area */}
        <div className="flex-1 overflow-y-auto p-4">
          {source.facets.length === 0 ? (
            <Card className="p-12 text-center bg-warm-50/50">
              <FileText className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-muted mb-2">No facets detected yet</p>
              <p className="text-sm text-text-muted">
                Click "Analyze Content" to extract insights from this document
              </p>
            </Card>
          ) : viewMode === 'list' ? (
            /* List View - Grouped by category */
            <div className="space-y-4">
              {(Object.entries(FACET_CATEGORY_CONFIG) as [FacetCategory, typeof FACET_CATEGORY_CONFIG[FacetCategory]][]).map(([category, config]) => {
                const categoryFacets = facetsByCategory[category] || []
                const filteredCategoryFacets = categoryFacets.filter(f => {
                  if (filterCategory !== 'all' && filterCategory !== category) return false
                  if (filterStatus !== 'all' && f.status !== filterStatus) return false
                  return true
                })

                if (filteredCategoryFacets.length === 0) return null

                const CategoryIcon = CATEGORY_ICONS[category]
                const isExpanded = expandedCategories.has(category)

                return (
                  <div key={category} className="border border-warm-200 rounded-lg overflow-hidden">
                    {/* Category Header */}
                    <div
                      className="flex items-center gap-2 p-3 cursor-pointer hover:bg-warm-50 transition-colors bg-warm-50/30"
                      onClick={() => toggleCategory(category)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-text-muted" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-text-muted" />
                      )}
                      <div
                        className="w-6 h-6 rounded flex items-center justify-center"
                        style={{ backgroundColor: `${config.color}20` }}
                      >
                        <CategoryIcon className="w-3.5 h-3.5" style={{ color: config.color }} />
                      </div>
                      <span className="font-medium" style={{ color: config.color }}>
                        {config.label}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {filteredCategoryFacets.length}
                      </Badge>
                    </div>
                    {/* Category Content */}
                    {isExpanded && (
                      <div className="space-y-2 p-3 pt-0">
                        {filteredCategoryFacets.map(facet => (
                          <FacetCard
                            key={facet.id}
                            facet={facet}
                            isSelected={selectedFacetId === facet.id}
                            onClick={() => setSelectedFacetId(facet.id === selectedFacetId ? null : facet.id)}
                            onAccept={() => handleFacetDecision(facet.id, 'accept')}
                            onReject={() => handleFacetDecision(facet.id, 'reject')}
                            onCreateEntity={() => handleCreateEntity(facet.id)}
                            isCreating={creatingEntity === facet.id}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : viewMode === 'pdf' && pdfData ? (
            /* PDF View - Native PDF rendering */
            <PdfViewer
              fileData={pdfData}
              textFacets={source.facets}
              selectedFacetId={selectedFacetId}
              onFacetClick={(id) => setSelectedFacetId(id === selectedFacetId ? null : id)}
              className="h-full"
            />
          ) : (
            /* Doc View - Smart document with highlights */
            <SmartDocView
              content={source.markdownContent}
              facets={source.facets}
              selectedFacetId={selectedFacetId}
              onFacetClick={(id) => setSelectedFacetId(id === selectedFacetId ? null : id)}
              filterCategory={filterCategory}
              filterStatus={filterStatus}
            />
          )}
        </div>

        {/* Detail Panel */}
        {selectedFacet && (
          <FacetDetailPanel
            facet={selectedFacet}
            onClose={() => setSelectedFacetId(null)}
            onAccept={() => handleFacetDecision(selectedFacet.id, 'accept')}
            onReject={() => handleFacetDecision(selectedFacet.id, 'reject')}
            onCreateEntity={() => handleCreateEntity(selectedFacet.id)}
            isCreating={creatingEntity === selectedFacet.id}
            entityTypes={entityTypes}
            onChangeEntityType={(type) => {
              handleFacetDecision(selectedFacet.id, 'accept', type)
            }}
          />
        )}
      </div>
    </div>
  )
}
